import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Select, Tooltip } from 'antd';
import App from 'antd/lib/app';
import { AxiosResponse } from 'axios';
import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { API } from '../../api';
import {
  completeMultipartUploadAsync,
  initMultipartUpload,
  uploadChunkAsync,
} from '../../api/uploadFileWithChunks';
import { PageHeader } from '../../components/common/page-header';
import {
  LS_LAYOUT_VALUE_LEY_PLAYLIST_PAGE,
  LS_LAYOUT_VALUE_LEY_VIDEO_PAGE,
} from '../../components/common/title-with-layout';
import { UploadVideoVariantsPopover } from '../../components/common/upload-video-variants-popover';
import AddVideo from '../../components/icons/add-video';
import LayoutGridIcon from '../../components/icons/layout-grid-icon';
import LayoutTableIcon from '../../components/icons/layout-table-icon';
import { Content } from '../../components/layout/upload-video-layout';
import { UploadProgressBar } from '../../components/upload-progress';
import { NotificationsTrafficLimit } from '../../components/uploaded-videos/NotificationsTrafficLimit';
import { PageMenu } from '../../components/uploaded-videos/page-menu';
import { PlaylistModuleAssets } from '../../components/uploaded-videos/playlist-module-assets';
import { Playlists } from '../../components/uploaded-videos/playlists';
import { Videos } from '../../components/uploaded-videos/videos';
import { getDefaultLinkSettings } from '../../constants/default-link-settings';
import { useHandleLoadPlaylist } from '../../hooks/playlists';
import { useLocalStorageValue } from '../../hooks/use-local-storage-value';
import { usePlaylistHandleSave } from '../../hooks/use-playlist-handle-save';
import { LocaleKeys } from '../../locale';
import { PlaylistCreateModal } from '../../modals/video/playlist-create-modal';
import { UploadWithLinkModal } from '../../modals/video/upload-with-link-modal';
import { useAppDispatch } from '../../state';
import {
  useMeSelector,
  useMeTariffLimits,
  useMeTariffPlan,
} from '../../state/me/selectors';
import { usePlaylistCurrentSelector } from '../../state/playlists/selectors';
import { setProgress } from '../../state/upload-progress/uploadProgressSlice';
import {
  TFirstStage,
  createVideoNextStageAction,
  createVideoResetStateAction,
  createVideoSetFirstStageAction,
} from '../../state/video/createSlice';
import { useCreateVideoSelector } from '../../state/video/selectors';
import { Paths, SecondaryPaths } from '../../types/common';
import { TUser } from '../../types/responses/users';
import {
  TCreatedVideo,
  TPlaylistContentItem,
} from '../../types/responses/video';
import {
  EPlayerMode,
  ESortOption,
  EVideoQueryMods,
  EVideoQueryPaths,
  ModeOptions,
} from '../../types/video';
import { useGetVideoDuration } from '../../utils/useGetVideoDuration';
import { useWidth } from '../../utils/useWidth';
import { PlaylistModule, playlistModules } from '../playlist-module-data';
import { UploadResponse, VideoItem } from './types';
import {
  AddVideoButton,
  ContentWrapper,
  FlexTitle,
  LayoutWrapper,
  LoadingWrapper,
  PlaylistSelectionLabel,
  PlaylistSelectionToolbar,
  TableNotificationsContainer,
  Title,
  TooltipWrapper,
} from './uploaded-videos.styled';

const formatFileSize = (sizeBytes: number) => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }
  if (sizeBytes < 1024 * 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const UploadedVideos: FC = () => {
  const [withSaving, setWithSaving] = useState(false);
  const [sortOption, setSortOption] = useState<ESortOption>(ESortOption.NEW);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [playListModal, setPlayListModal] = useState(false);
  const [playlistEditId, setPlayListEditId] = useState<string | null>(null);
  const [playlistEditMode, setPlayListEditMode] = useState(false);
  const [bandwidthRestPercent, setBandwidthRestPercent] = useState(100);
  const [loadWithLinkModalOpen, setLoadWithLinkModalOpen] = useState(false);
  const [isPlayListPage, setIsPlayListPage] = useState(false);
  const [isPlayCoursePage, setIsPlayCoursePage] = useState(false);
  const [loadedVideos, setLoadedVideos] = useState<TCreatedVideo[]>([]);
  const [selectedAssetKeys, setSelectedAssetKeys] = useState<string[]>([]);
  const [playerMode, setPlayerMode] = useState<EPlayerMode>(
    EPlayerMode.Training
  );
  const [students, setStudents] = useState<TUser[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [preparingLinks, setPreparingLinks] = useState(false);
  const [availableModules, setAvailableModules] =
    useState<PlaylistModule[]>(playlistModules);

  const location = useLocation();
  const { t } = useTranslation([LocaleKeys.VIDEO]);
  const { width } = useWidth();

  const tariffPlan = useMeTariffPlan();
  const tariffLimits = useMeTariffLimits();
  const { meData } = useMeSelector();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { message } = App.useApp();

  const { currentStage } = useCreateVideoSelector();

  const { data, loaded, pending } = usePlaylistCurrentSelector();

  const [searchParams] = useSearchParams();

  const [layout, setLayout] = useLocalStorageValue<'colvideo' | 'gridvideo'>(
    'gridvideo',
    LS_LAYOUT_VALUE_LEY_VIDEO_PAGE
  );

  const [layoutPalylist, setLayoutPlaylist] = useLocalStorageValue<
    'colplay' | 'gridplay'
  >('gridplay', LS_LAYOUT_VALUE_LEY_PLAYLIST_PAGE);

  const [
    handleSavePlaylist,
    operationProps,
    exitEditMode,
    editSelected,
    setEditSelected,
  ] = usePlaylistHandleSave();

  const inputRef = useRef<HTMLInputElement>(null);
  const anyFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsPlayListPage(location.pathname.includes(SecondaryPaths.PLAYLISTS));
    setIsPlayCoursePage(
      location.pathname.includes(SecondaryPaths.PLAYCOURSES)
    );
  }, [location]);

  const handleChangeVideoList = useCallback((videoId: string) => {
    setEditSelected((prev) => {
      if (prev.includes(videoId)) {
        return prev.filter((el) => el !== videoId);
      } else {
        return [...prev, videoId];
      }
    });
  }, []);

  const moduleAssets = useMemo(
    () =>
      availableModules.flatMap((module) =>
        module.folders.flatMap((folder) =>
          folder.files.map((file) => ({
            ...file,
            moduleId: module.id,
            moduleName: module.name,
            folderName: folder.name,
          }))
        )
      ),
    [availableModules]
  );

  const handleToggleAsset = useCallback((s3Key: string) => {
    setSelectedAssetKeys((current) =>
      current.includes(s3Key)
        ? current.filter((key) => key !== s3Key)
        : [...current, s3Key]
    );
  }, []);

  const allItemsSelected =
    loadedVideos.length + moduleAssets.length > 0 &&
    editSelected.length === loadedVideos.length &&
    selectedAssetKeys.length === moduleAssets.length;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setEditSelected(checked ? loadedVideos.map((video) => video.id) : []);
      setSelectedAssetKeys(
        checked ? moduleAssets.map((asset) => asset.s3Key) : []
      );
    },
    [loadedVideos, moduleAssets, setEditSelected]
  );

  const handleLoadPlaylist = useHandleLoadPlaylist();

  useEffect(() => {
    const playlistId = searchParams.get(EVideoQueryPaths.PLAYLIST_ID);
    const mode = searchParams.get(EVideoQueryPaths.MODE);

    if (mode === EVideoQueryMods.ADD_VIDEO && playlistId) {
      setPlayListEditMode(true);
      setPlayListEditId(playlistId);
    } else {
      setPlayListEditMode(false);
      setPlayListEditId(null);
    }
  }, [searchParams]);

  useLayoutEffect(() => {
    if (currentStage === 'second') {
      navigate(Paths.VIDEO_CREATE);
    }
  }, [currentStage]);

  const handleSaveStage = useCallback((stageData: Partial<TFirstStage>) => {
    dispatch(createVideoSetFirstStageAction(stageData));
  }, []);

  const initUploadWithChunks = useCallback(
    async (fileName: string, fileType: string, fileSize: number) => {
      try {
        dispatch(setProgress({ progress: 0, visible: true }));
        setIsUploading(true);
        const responseInitUpload: AxiosResponse<UploadResponse> =
          await initMultipartUpload(fileName, fileType, fileSize);

        const key = responseInitUpload.data?.key;
        const uploadId = responseInitUpload.data?.uploadId;

        if (key && uploadId) {
          return { key, uploadId };
        }

        console.error('initMultipartUpload returned an incomplete response:', {
          key,
          uploadId,
          data: responseInitUpload.data,
        });
        dispatch(setProgress({ progress: 0, visible: false }));
        setIsUploading(false);
        return { key: undefined, uploadId: undefined };
      } catch (err: any) {
        const responseMessage = Array.isArray(err?.response?.data?.message)
          ? err.response.data.message.join(', ')
          : (err?.response?.data?.message ?? err?.message ?? '');

        console.error('initMultipartUpload failed:', {
          status: err?.response?.status,
          message: responseMessage,
          data: err?.response?.data,
        });

        if (
          responseMessage.includes(
            'The video size limit for the tariff plan has been exceeded'
          ) ||
          responseMessage.includes('Storage limit exceeded')
        ) {
          message.error(t('upload.errorSizeLimit'));
        } else {
          message.error(t('upload.error'));
        }
        dispatch(setProgress({ progress: 0, visible: false }));
        setIsUploading(false);
        return { key: undefined, uploadId: undefined };
      }
    },
    []
  );
  const completeUpload = useCallback(
    async (
      uploadId: string,
      key: string,
      videoItem: VideoItem,
      fileDuration: number
    ) => {
      try {
        const response: AxiosResponse = await completeMultipartUploadAsync({
          uploadId,
          key,
        });

        if ([200, 201].includes(response.status)) {
          message.success(t('upload.success'));
          handleSaveStage({
            videoDuration: fileDuration,
            uploadResponse: { ...response.data },
            uploaded: true,
            pending: false,
            filled: true,
          });
          dispatch(setProgress({ progress: 0, visible: false }));
          setIsUploading(false);
          dispatch(createVideoNextStageAction());
          videoItem.remove();
        } else {
          throw new Error('Failed to complete upload');
        }
      } catch (error) {
        console.error('Error completing upload:', error);
        message.error(t('upload.error'));
        dispatch(setProgress({ progress: 0, visible: false }));
        setIsUploading(false);
        handleSaveStage({ pending: false });
      }
    },
    []
  );

  const uploadChunks = useCallback(
    async (
      key: string,
      uploadId: string,
      file: File,
      videoItem: VideoItem,
      fileDuration: number
    ) => {
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const MAX_RETRIES = 3; // Максимальное количество попыток
      let chunkIndex = 0;
      const failedChunks = new Map<number, number>();

      const uploadChunk = async (partNumber: number) => {
        if (
          failedChunks.get(partNumber) &&
          failedChunks.get(partNumber)! >= MAX_RETRIES
        ) {
          console.error(
            `Chunk ${partNumber} failed after ${MAX_RETRIES} attempts.`
          );
          return false;
        }

        const startByte = (partNumber - 1) * CHUNK_SIZE;
        const endByte = Math.min(partNumber * CHUNK_SIZE, file.size);
        const chunk = file.slice(startByte, endByte);
        const fdChunk = new FormData();
        fdChunk.append('chunk', chunk);
        fdChunk.append('uploadId', uploadId);
        fdChunk.append('partNumber', String(partNumber));
        fdChunk.append('key', key);

        try {
          const response = await uploadChunkAsync(fdChunk);

          if (![200, 201].includes(response.status)) {
            throw new Error(
              `Chunk ${partNumber} failed with status ${response.status}`
            );
          }

          failedChunks.delete(partNumber);
          chunkIndex++;
          const newProgress = Math.round((chunkIndex / totalChunks) * 100);
          dispatch(setProgress({ progress: newProgress, visible: true }));

          return true;
        } catch (error) {
          console.error(`Chunk ${partNumber} upload error:`, error);
          failedChunks.set(partNumber, (failedChunks.get(partNumber) || 0) + 1);

          if (failedChunks.get(partNumber)! < MAX_RETRIES) {
            console.log(
              `Retrying chunk ${partNumber}... (${failedChunks.get(partNumber)} attempt)`
            );
            await new Promise((res) => setTimeout(res, 3000));
            return await uploadChunk(partNumber);
          } else {
            console.error(`Chunk ${partNumber} failed permanently.`);
            return false;
          }
        }
      };

      for (let i = 1; i <= totalChunks; i++) {
        const success = await uploadChunk(i);
        if (!success) break;
      }

      if (failedChunks.size > 0) {
        console.error(
          `Upload failed for chunks: ${Array.from(failedChunks.keys()).join(', ')}`
        );
        dispatch(setProgress({ progress: 0, visible: false }));
        setIsUploading(false);
        message.error(t('upload.error'));
        return;
      }

      await completeUpload(uploadId, key, videoItem, fileDuration);
    },
    [t, completeUpload]
  );

  const handleUploadVideo = useCallback(
    async (fileFormData: FormData) => {
      try {
        handleSaveStage({ pending: true });

        const videoItem = document.createElement('video');
        videoItem.preload = 'metadata';

        const file = fileFormData.get('file') as File | null;

        if (!file) {
          handleSaveStage({ pending: false });
          return;
        }

        const canPlay = videoItem.canPlayType(file.type);

        if (!canPlay) {
          handleSaveStage({ pending: false });
          message.error(t('upload.unsupportedFormat'));
          return;
        }

        const fileDuration = await useGetVideoDuration(file);

        const fileSize = file.size;

        handleSaveStage({ file });

        const { key, uploadId } = await initUploadWithChunks(
          file.name,
          file.type,
          fileSize
        );

        if (key && uploadId) {
          await uploadChunks(key, uploadId, file, videoItem, fileDuration);
        } else {
          handleSaveStage({ pending: false });
        }
      } catch (error) {
        console.error('Video error:', error);
        handleSaveStage({ pending: false });
      }
    },
    [t]
  );

  const handleChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.item(0)) {
        const file = target.files.item(0);

        if (file) {
          const formData = new FormData();
          formData.set('file', file);
          formData.set('mediaType', 'video');

          if (file.size < 2147483648) {
            void handleUploadVideo(formData);
          } else {
            void message.error(t('upload.sizeLimitError'));
          }

          if (inputRef.current) {
            // @ts-ignore
            inputRef.current.value = null;
          }
        }
      }
      dispatch(createVideoResetStateAction());
    },
    [t]
  );

  const handleUploadAnyFile = useCallback(
    async (fileFormData: FormData) => {
      try {
        handleSaveStage({ pending: true });

        const file = fileFormData.get('file') as File | null;

        if (!file) {
          handleSaveStage({ pending: false });
          return;
        }

        const fileSize = file.size;
        handleSaveStage({ file });

        const { key, uploadId } = await initUploadWithChunks(
          file.name,
          file.type,
          fileSize
        );

        if (key && uploadId) {
          const dummyVideoItem = document.createElement('video');
          await uploadChunks(key, uploadId, file, dummyVideoItem, 0);
        } else {
          handleSaveStage({ pending: false });
        }
      } catch (error) {
        console.error('File upload error:', error);
        handleSaveStage({ pending: false });
      }
    },
    [t, initUploadWithChunks, uploadChunks, handleSaveStage]
  );

  const handleAnyFileChange = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.item(0)) {
        const file = target.files.item(0);

        if (file) {
          const formData = new FormData();
          formData.set('file', file);
          formData.set('mediaType', 'video'); // backend uses this key internally

          if (file.size < 2147483648) {
            void handleUploadAnyFile(formData);
          } else {
            void message.error(t('upload.sizeLimitError'));
          }

          if (anyFileInputRef.current) {
            // @ts-ignore
            anyFileInputRef.current.value = null;
          }
        }
      }
      dispatch(createVideoResetStateAction());
    },
    [t, handleUploadAnyFile, dispatch]
  );

  useEffect(() => {
    if (
      playlistEditMode &&
      playlistEditId &&
      ((!pending && !loaded) || (loaded && data?.id !== playlistEditId))
    ) {
      void handleLoadPlaylist(playlistEditId);
    }
  }, [loaded, pending, data, playlistEditId, playlistEditMode]);

  useEffect(() => {
    if (data && playlistEditMode && !pending) {
      const parameterizedVideoIds =
        data.items
          ?.filter((item) => item.type === 'video' && item.sourceId)
          .map((item) => item.sourceId as string) ?? [];

      setEditSelected(
        parameterizedVideoIds.length
          ? parameterizedVideoIds
          : (data.videos?.map((el) => el.id) ?? [])
      );
      setSelectedAssetKeys(
        data.items
          ?.filter((item) => item.type === 'asset' && item.s3Key)
          .map((item) => item.s3Key as string) ?? []
      );
      setSelectedStudentIds(data.assignees?.map((user) => user.id) ?? []);
      setPlayerMode(data.items?.[0]?.playerMode ?? EPlayerMode.Training);
    }
  }, [data, playlistEditMode, pending]);

  useEffect(() => {
    if (!playlistEditMode) {
      return;
    }

    void API.streams
      .getEducationAssets()
      .then((response) => {
        const foldersByModule = new Map<
          number,
          Map<string, PlaylistModule['folders'][number]>
        >();

        response.data.forEach((asset) => {
          const [, moduleSegment, folderName] = asset.s3Key.split('/');
          const moduleNumber = Number.parseInt(moduleSegment, 10);
          if (
            !Number.isInteger(moduleNumber) ||
            moduleNumber < 1 ||
            moduleNumber > 7 ||
            !folderName
          ) {
            return;
          }

          const extension = asset.name.split('.').pop()?.toLowerCase();
          const kind =
            extension === 'mp4'
              ? 'video'
              : extension === 'pdf'
                ? 'pdf'
                : extension === 'txt'
                  ? 'text'
                  : extension === 'csv'
                    ? 'csv'
                    : extension === 'xls' || extension === 'xlsx'
                      ? 'excel'
                      : 'document';
          const moduleFolders =
            foldersByModule.get(moduleNumber) ??
            new Map<string, PlaylistModule['folders'][number]>();
          const folder =
            moduleFolders.get(folderName) ??
            ({
              id: `${moduleNumber}-${folderName}`,
              name: folderName.replace(/_/g, ' '),
              date: '',
              time: '',
              files: [],
            } as PlaylistModule['folders'][number]);

          folder.files.push({
            name: asset.name,
            kind,
            size: formatFileSize(asset.sizeBytes),
            s3Key: asset.s3Key,
            minimumQualifiedDurationMs:
              asset.minimumQualifiedDurationMs,
          });
          moduleFolders.set(folderName, folder);
          foldersByModule.set(moduleNumber, moduleFolders);
        });

        const modules = Array.from({ length: 7 }, (_, index) => {
          const moduleNumber = index + 1;
          return {
            id: `module${moduleNumber}`,
            name: `Module ${moduleNumber}`,
            viewPath: `/module${moduleNumber}/view`,
            folders: Array.from(
              foldersByModule.get(moduleNumber)?.values() ?? []
            ),
          };
        });

        if (modules.some((module) => module.folders.length > 0)) {
          setAvailableModules(modules);
        }
      })
      .catch(() => {
        setAvailableModules(playlistModules);
      });

    void API.playlists
      .getStudents()
      .then((response) => setStudents(response.data))
      .catch(() => {
        setStudents([]);
        void message.error('Unable to load students');
      });
  }, [playlistEditMode, message]);

  const handleSavePlaylistWithLinks = useCallback(async () => {
    if (!data) {
      return;
    }

    setPreparingLinks(true);
    const settings = getDefaultLinkSettings(playerMode);
    const existingItems = data.items ?? [];
    const items: TPlaylistContentItem[] = [];

    try {
      for (const video of loadedVideos.filter((item) =>
        editSelected.includes(item.id)
      )) {
        const existing = existingItems.find(
          (item) =>
            item.type === 'video' &&
            item.sourceId === video.id &&
            item.playerMode === playerMode
        );

        if (existing) {
          items.push(existing);
          continue;
        }

        const response = await API.videos.createLink(
          { videoId: video.id },
          { ...settings, title: video.title }
        );
        items.push({
          type: 'video',
          sourceId: video.id,
          linkId: response.data.id,
          name: video.title,
          kind: 'video',
          playerMode,
          previewUrl: video.videoPreview?.url ?? null,
          duration: video.duration ?? null,
        });
      }

      for (const assetKey of selectedAssetKeys) {
        const existing = existingItems.find(
          (item) =>
            item.type === 'asset' &&
            item.s3Key === assetKey &&
            item.playerMode === playerMode
        );

        if (existing) {
          items.push(existing);
          continue;
        }

        const asset = moduleAssets.find((item) => item.s3Key === assetKey);

        if (!asset) {
          continue;
        }

        const response = await API.assetLinks.create({
          s3Key: asset.s3Key,
          name: asset.name,
          title: asset.name,
          settings: {
            ...settings,
            title: asset.name,
            ...(asset.minimumQualifiedDurationMs
              ? {
                  documentMinimumQualifiedDurationMs:
                    asset.minimumQualifiedDurationMs,
                }
              : {}),
          },
          kind: asset.kind,
          size: asset.size,
          isAuthRequired: true,
        });
        items.push({
          type: 'asset',
          s3Key: asset.s3Key,
          linkId: response.data.id,
          name: asset.name,
          kind: asset.kind,
          size: asset.size,
          moduleId: asset.moduleId,
          moduleName: asset.moduleName,
          playerMode,
        });
      }

      await handleSavePlaylist({
        items,
        assigneeIds: selectedStudentIds,
      });
    } catch (error) {
      console.error('Unable to create playlist links:', error);
      void message.error('Unable to create parameterized playlist links');
    } finally {
      setPreparingLinks(false);
    }
  }, [
    data,
    editSelected,
    handleSavePlaylist,
    loadedVideos,
    message,
    moduleAssets,
    playerMode,
    selectedAssetKeys,
    selectedStudentIds,
  ]);

  const handleUploadByLink = useCallback(() => {
    setWithSaving(true);
    setLoadWithLinkModalOpen(true);
  }, []);

  const handleLoadLinkWithoutUpload = useCallback(() => {
    setWithSaving(false);
    setLoadWithLinkModalOpen(true);
  }, []);

  useEffect(() => {
    if (tariffLimits && tariffPlan) {
      const percentRest =
        (tariffLimits.bandwidth_rest * 100) / tariffPlan.bandwidth;

      setBandwidthRestPercent(percentRest);
    }
  }, [tariffLimits, tariffPlan]);

  const handleChangeLayoutVideo = useCallback(() => {
    setLayout(layout === 'colvideo' ? 'gridvideo' : 'colvideo');
  }, [layout]);

  const handleChangeLayoutPlaylist = useCallback(() => {
    setLayoutPlaylist(layoutPalylist === 'colplay' ? 'gridplay' : 'colplay');
  }, [layoutPalylist]);

  return (
    <div>
      <input
        type="file"
        hidden
        id="add-video-file"
        ref={inputRef}
        onChange={handleChange}
        accept="video/*"
      />

      <input
        type="file"
        hidden
        id="add-any-file"
        ref={anyFileInputRef}
        onChange={handleAnyFileChange}
        accept="*"
      />

      <PageHeader
        rightAddon={
          <UploadVideoVariantsPopover
            onUploadWithLinkClick={handleUploadByLink}
            onWithoutUploadClick={handleLoadLinkWithoutUpload}
          >
            <AddVideoButton>
              <AddVideo />
              <p>{t('videos.download.title')}</p>
            </AddVideoButton>
          </UploadVideoVariantsPopover>
        }
      >
        <FlexTitle>
          <Title>{t('videos.uploaded')}</Title>
          {width > 948 && bandwidthRestPercent < 20 && (
            <NotificationsTrafficLimit />
          )}
        </FlexTitle>
      </PageHeader>

      {width <= 948 && bandwidthRestPercent < 20 && (
        <TableNotificationsContainer>
          <NotificationsTrafficLimit />
        </TableNotificationsContainer>
      )}

      <PageMenu
        playlistEditMode={playlistEditMode}
        setPlayListModal={setPlayListModal}
        handleSavePlaylist={() => void handleSavePlaylistWithLinks()}
        exitEditMode={exitEditMode}
        operationsButtonProps={{
          ...operationProps,
          loading: preparingLinks || operationProps?.loading,
        }}
        sortOption={sortOption}
        setSortOption={setSortOption}
        showNavigationLinks={false}
        createMode={isPlayCoursePage ? 'playcourse' : 'playlist'}
        blockEditPlaylist={meData?.role !== 2 && meData?.role !== 3}
      />

      <TooltipWrapper>
        <Tooltip placement="left" title={t('playlist.sort')}>
          <LayoutWrapper
            onClick={
              isPlayListPage || isPlayCoursePage
                ? handleChangeLayoutPlaylist
                : handleChangeLayoutVideo
            }
          >
            {layout.includes('col') ? <LayoutTableIcon /> : <LayoutGridIcon />}
          </LayoutWrapper>
        </Tooltip>
      </TooltipWrapper>

      {playlistEditMode ? (
        <>
          <PlaylistSelectionToolbar>
            <Checkbox
              checked={allItemsSelected}
              indeterminate={
                !allItemsSelected &&
                editSelected.length + selectedAssetKeys.length > 0
              }
              onChange={(event) => handleSelectAll(event.target.checked)}
            >
              Select all
            </Checkbox>
            <PlaylistSelectionLabel>Player mode</PlaylistSelectionLabel>
            <Select
              className="playlist-mode"
              value={playerMode}
              onChange={setPlayerMode}
              options={Object.values(EPlayerMode).map((mode) => ({
                value: mode,
                label: t(ModeOptions[mode]),
              }))}
            />
            <PlaylistSelectionLabel>Students</PlaylistSelectionLabel>
            <Select
              className="playlist-students"
              mode="multiple"
              allowClear
              placeholder="Assign playlist to students"
              value={selectedStudentIds}
              onChange={setSelectedStudentIds}
              options={students.map((student) => ({
                value: student.id,
                label:
                  [student.firstName, student.lastName]
                    .filter(Boolean)
                    .join(' ') || student.email,
              }))}
            />
          </PlaylistSelectionToolbar>
          <ContentWrapper
            layout={
              isPlayListPage || isPlayCoursePage ? layoutPalylist : layout
            }
          >
            {pending && (
              <LoadingWrapper>
                <LoadingOutlined />
              </LoadingWrapper>
            )}
            <Videos
              playlistEditMode={playlistEditMode}
              handleChangeVideoList={handleChangeVideoList}
              selectedVideos={editSelected}
              sortOption={sortOption}
              layout={
                isPlayListPage || isPlayCoursePage ? layoutPalylist : layout
              }
              onLoadedVideos={setLoadedVideos}
            />
            <PlaylistModuleAssets
              modules={availableModules}
              selectedKeys={selectedAssetKeys}
              onToggle={handleToggleAsset}
            />
          </ContentWrapper>
        </>
      ) : (
        <Routes>
          <Route
            element={
              <Content
                layout={
                  isPlayListPage || isPlayCoursePage ? layoutPalylist : layout
                }
              />
            }
          >
            <Route
              path={SecondaryPaths.BASE}
              element={
                <Videos
                  layout={isPlayListPage ? layoutPalylist : layout}
                  sortOption={sortOption}
                />
              }
            />
            <Route
              path={SecondaryPaths.PLAYLISTS}
              element={
                <Playlists
                  layout={isPlayListPage ? layoutPalylist : layout}
                  sortOption={sortOption}
                />
              }
            />
            <Route
              path={SecondaryPaths.PLAYCOURSES}
              element={
                <Playlists
                  coursesOnly
                  layout={isPlayCoursePage ? layoutPalylist : layout}
                  sortOption={sortOption}
                />
              }
            />
          </Route>
        </Routes>
      )}

      <PlaylistCreateModal
        isCourse={isPlayCoursePage}
        open={playListModal}
        setOpen={setPlayListModal}
      />

      <UploadWithLinkModal
        open={loadWithLinkModalOpen}
        setOpen={setLoadWithLinkModalOpen}
        withSaving={withSaving}
      />

      {isUploading && <UploadProgressBar />}
    </div>
  );
};
